// Auto-generated lightweight class for MBSE_Attribute
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { MBSE_AttributeReadAPI, MBSE_AttributeWriteAPI, MBSE_AttributeReadAPISchema, MBSE_AttributeWriteAPISchema } from '../types/mbse_attribute';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasMembers, MBSE_Attributes, MBSE_Bind } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { MBSE_Requirement } from './mbse_requirement';



export interface MBSE_Attribute extends Partial<MBSE_AttributeReadAPI> {}
export class MBSE_Attribute extends NeoNodeBase<MBSE_AttributeReadAPI, MBSE_AttributeWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "MBSE_Attribute";
  static readonly readSchema = MBSE_AttributeReadAPISchema;
  static readonly writeSchema = MBSE_AttributeWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    subjects: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Attributes", direction: "in" },
    binds_to: { target: "MBSE_Attribute", cardinality: "many", isParent: false, relClass: "MBSE_Bind", direction: "out" },
    bound_in: { target: "MBSE_Attribute", cardinality: "many", isParent: false, relClass: "MBSE_Bind", direction: "in" },
  };

  protected get modelName(): string {
    return 'MBSE_Attribute';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return MBSE_Attribute.label;
  }


  constructor(data: Partial<MBSE_AttributeWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: MBSE_AttributeReadAPI, loadContext?: Record<string, any>): MBSE_Attribute {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, subjects, binds_to, bound_in, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = MBSE_AttributeReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, subjects: subjects, binds_to: binds_to, bound_in: bound_in };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return MBSE_Attribute.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'subjects', 'MBSE_Requirement', ModelRegistry);
    loadManyRelationship(processed, 'binds_to', 'MBSE_Attribute', ModelRegistry);
    loadManyRelationship(processed, 'bound_in', 'MBSE_Attribute', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new MBSE_Attribute(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'subjects' - Python ORM style (cached)
  get subjects(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('subjects', () => {
      return new RelationshipManager(this, 'subjects', 'many', MBSE_Attributes as typeof Relationship);
    });
  }

  // Relationship manager for 'binds_to' - Python ORM style (cached)
  get binds_to(): RelationshipManager<this, MBSE_Attribute> {
    return this.getOrCreateRelationshipManager('binds_to', () => {
      return new RelationshipManager(this, 'binds_to', 'many', MBSE_Bind as typeof Relationship);
    });
  }

  // Relationship manager for 'bound_in' - Python ORM style (cached)
  get bound_in(): RelationshipManager<this, MBSE_Attribute> {
    return this.getOrCreateRelationshipManager('bound_in', () => {
      return new RelationshipManager(this, 'bound_in', 'many', MBSE_Bind as typeof Relationship);
    });
  }

}
